import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import WeatherWidget from './WeatherWidget';
import { AuthButton } from './AuthButton';
import ContactFormModal from './ContactFormModal';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scrolling to hash on page load or navigation
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  // Close admin dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isAdminMenuOpen && !target.closest('.admin-dropdown')) {
        setIsAdminMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAdminMenuOpen]);

  // Check if user has inspector or admin role
  const hasInspectionAccess = () => {
    if (!user) return false;
    const role = user.publicMetadata?.role as string | undefined;
    return ['admin', 'lead_inspector', 'inspector'].includes(role || '');
  };

  // Check if user is admin
  const isAdmin = () => {
    if (!user) return false;
    const role = user.publicMetadata?.role as string | undefined;
    return role === 'admin';
  };

  // Check if user is a customer (logged in but not inspector/admin)
  const isCustomer = () => {
    if (!user) return false;
    return !hasInspectionAccess();
  };

  // Handle hash link navigation (for home page sections)
  const handleHashClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    hash: string
  ) => {
    e.preventDefault();
    setIsOpen(false);

    // If clicking on #contact, open the modal instead
    if (hash === '#contact') {
      setIsContactModalOpen(true);
      return;
    }

    // If we're on the home page, just scroll
    if (location.pathname === '/') {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Navigate to home page with hash, then scroll
      navigate('/' + hash);
    }
  };

  // Main navigation (public + home page sections)
  const mainNavLinks = [
    { href: '/services/home-watch', label: 'Home Watch', isHash: false },
    { href: '/services/concierge', label: 'Concierge', isHash: false },
    { href: '/blog', label: 'Blog', isHash: false },
    { href: '/about', label: 'About Us', isHash: false },
    { href: '/faq', label: 'FAQ', isHash: false },
    { href: '#contact', label: 'Contact', isHash: true },
  ];

  // Admin dropdown links
  const adminLinks = [
    { href: '/manage-properties', label: 'Properties', isRoute: true },
    { href: '/manage-customers', label: 'Customers', isRoute: true },
    { href: '/admin/inspections', label: 'Inspections', isRoute: true },
    { href: '/admin/ad-hoc-services', label: 'Ad-Hoc Services', isRoute: true },
    { href: '/studio', label: 'Studio', isRoute: true },
  ];

  // Role-specific action links
  const roleLinks = [
    {
      href: '/start-inspection',
      label: 'Start Inspection',
      isRoute: true,
      requiresInspectionAccess: true,
      prominent: true,
    },
    {
      href: '/customer-dashboard',
      label: 'My Dashboard',
      isRoute: true,
      requiresCustomerAccess: true,
      prominent: false,
    },
  ].filter((link) => {
    if (link.requiresInspectionAccess) {
      return hasInspectionAccess();
    }
    if (link.requiresCustomerAccess) {
      return isCustomer();
    }
    return true;
  });

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  return (
    <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 shadow-md border-b border-sand-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <Link
            to="/"
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <div className="shrink-0">
              <img
                className="h-16 sm:h-20 w-auto"
                src="/OnArrival_Global_Logo.png"
                alt="On Arrival Global Logo"
              />
            </div>
            <div className="ml-2 sm:ml-4">
              <div className="font-bold text-lg sm:text-3xl text-primary-500 tracking-tight">
                OnArrival Global
              </div>
              <p className="text-xs sm:text-lg italic text-primary-500">
                &ldquo;Your 2nd home, our 1st priority.&rdquo;
              </p>
            </div>
          </Link>

          {/* Weather Widget - Hidden on mobile, shown on tablet+ */}
          <div className="hidden sm:block">
            <WeatherWidget />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-6 items-center">
            {/* Main Navigation Links */}
            {mainNavLinks.map((link) =>
              link.isHash ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleHashClick(e, link.href)}
                  className="font-medium text-sand-800 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="font-medium text-sand-800 hover:text-primary-500 transition-colors whitespace-nowrap"
                >
                  {link.label}
                </Link>
              )
            )}

            {/* Role-specific Links */}
            {roleLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={
                  link.prominent
                    ? 'bg-primary-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap'
                    : 'font-medium text-sand-800 hover:text-primary-500 transition-colors whitespace-nowrap'
                }
              >
                {link.label}
              </Link>
            ))}

            {/* Admin Dropdown */}
            {isAdmin() && (
              <div className="relative admin-dropdown">
                <button
                  onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                  className="font-medium text-sand-800 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  Admin
                  <svg
                    className={`w-4 h-4 transition-transform ${isAdminMenuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {isAdminMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-sand-200 py-2 z-50">
                    {adminLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsAdminMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-sand-800 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            <AuthButton />
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-sand-600 hover:text-sand-800 hover:bg-sand-100 focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary-500"
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16m-7 6h7"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {/* Main Navigation */}
            {mainNavLinks.map((link) =>
              link.isHash ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleHashClick(e, link.href)}
                  className="block px-3 py-2 rounded-md text-base font-medium text-sand-800 hover:text-primary-700 hover:bg-sand-100 cursor-pointer"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={handleLinkClick}
                  className="block px-3 py-2 rounded-md text-base font-medium text-sand-800 hover:text-primary-700 hover:bg-sand-100"
                >
                  {link.label}
                </Link>
              )
            )}

            {/* Role-specific Links */}
            {roleLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={handleLinkClick}
                className={
                  link.prominent
                    ? 'block px-3 py-2 rounded-md text-base font-bold text-primary-500 hover:bg-primary-50'
                    : 'block px-3 py-2 rounded-md text-base font-medium text-sand-800 hover:text-primary-700 hover:bg-sand-100'
                }
              >
                {link.label}
              </Link>
            ))}

            {/* Admin Section */}
            {isAdmin() && (
              <div className="border-t border-sand-200 mt-2 pt-2">
                <div className="px-3 py-2 text-xs font-semibold text-sand-600 uppercase tracking-wider">
                  Admin
                </div>
                {adminLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={handleLinkClick}
                    className="block px-3 py-2 rounded-md text-base font-medium text-sand-800 hover:text-primary-700 hover:bg-sand-100"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            <div className="px-3 py-2 border-t border-sand-200 mt-2">
              <AuthButton />
            </div>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </header>
  );
};

export default Header;
